import { AlertTriangle, ChevronRight, Info } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import { cn } from '~/utils/cn';

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** `data-testid` for the dialog; derives `-title`, `-confirm-checkbox`, `-confirm-button`, `-step-{n}`. */
  testId?: string;
}

/** Pre-delivery confirmation modal for Meta Commerce Manager supplementary feed setup.
 *  Walks the user through 4 steps and requires a checkbox acknowledgement before confirming. */
export function MetaPreDeliveryConfirmation({
  open,
  onCancel,
  onConfirm,
  testId = 'meta-pre-delivery-dialog',
}: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [lastOpen, setLastOpen] = useState(open);
  // Render-time reset — avoids setState-in-effect. When the modal transitions
  // from closed to open the checkbox is cleared so the user must re-acknowledge.
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setConfirmed(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent
        data-testid={testId}
        className="flex max-h-[90vh] max-w-3xl flex-col gap-0 p-0"
      >
        {/* Scrollable body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-8 pt-8 pb-4">
          {/* Header */}
          <div className="space-y-3 text-center">
            <h2 data-testid={`${testId}-title`} className="text-2xl leading-tight font-bold">
              Activate Lily enrichments in Meta Commerce Manager
            </h2>
            <p className="text-stage-text text-sm leading-5">
              Lily has delivered your enriched product data directly to your Commerce Manager
              catalog. One step remains: you need to confirm the Lily supplementary feed is linked
              to your primary catalog so Meta can use it in your ads.
            </p>
          </div>

          {/* Why this matters */}
          <div className="border-primary/30 bg-primary/5 space-y-2 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Info className="text-primary h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="text-primary text-sm font-medium">Why this matters?</span>
            </div>
            <p className="text-stage-text text-sm leading-5">
              Meta's dynamic ads pull product content — titles, descriptions, attributes like color
              and material — directly from your Commerce Manager catalog. Without the supplementary
              feed linked, Meta serves ads using only your original catalog data. Confirming this
              connection is what switches your ads to Lily's enriched content.
            </p>
          </div>

          <hr className="border-border" />

          {/* Steps section label */}
          <p className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
            Steps to complete in Commerce Manager
          </p>

          {/* Steps */}
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={1} testId={`${testId}-step-1`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">Open your catalog</p>
                  <p className="text-stage-text text-sm leading-5">
                    Go to Meta Commerce Manager (
                    <a
                      href="https://business.facebook.com/business"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary hover:underline"
                      data-testid={`${testId}-step-1-link`}
                    >
                      business.facebook.com
                    </a>
                    ) and select your product catalog from the left sidebar.
                  </p>
                </div>
              </div>
              <PathBox>
                <a
                  href="https://business.facebook.com/business"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary hover:underline"
                  data-testid={`${testId}-step-1-path-link`}
                >
                  business.facebook.com
                </a>
                {' → select your catalog'}
              </PathBox>
            </div>

            {/* Step 2 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={2} testId={`${testId}-step-2`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">Go to Data sources</p>
                  <p className="text-stage-text text-sm leading-5">
                    In the left navigation, go to Catalog → Data sources. You'll see your existing
                    primary feed listed under Data feeds.
                  </p>
                </div>
              </div>
              <PathBox text="Catalog → Data sources" />
            </div>

            {/* Step 3 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={3} testId={`${testId}-step-3`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">
                    Confirm the Lily supplementary feed is listed
                  </p>
                  <p className="text-stage-text text-sm leading-5">
                    Scroll to the Supplementary feeds section. Lily's feed should appear here,
                    linked to your primary catalog. Confirm its status shows as active with no
                    critical errors.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <InfoSubBox
                  title="How Meta applies supplementary data."
                  body="Meta matches records in the Lily feed to your catalog using the product content ID. Lily's enriched attributes — including titles, descriptions, and category data — are added to each matched product and used in ad delivery automatically once the feed is active."
                />
                <div className="border-warning/40 bg-warning-50 flex items-start gap-3 rounded-lg border p-4">
                  <AlertTriangle
                    className="text-warning mt-0.5 h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-stage-text text-sm leading-5">
                    If the Lily feed is not listed, contact your CSM before proceeding.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={4} testId={`${testId}-step-4`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">
                    Check a product to verify enriched attributes are visible
                  </p>
                  <p className="text-stage-text text-sm leading-5">
                    In Commerce Manager, open any product from your catalog and select View more
                    feeds. Confirm you can see Lily's enriched attribute values alongside the
                    original primary feed values. This confirms the supplementary feed is active and
                    matched correctly.
                  </p>
                </div>
              </div>
              <PathBox text="Catalog → Items → [any product] → View more feeds" />
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex items-center justify-between gap-4 border-t px-6 py-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="meta-confirm-checkbox"
              data-testid={`${testId}-confirm-checkbox`}
              checked={confirmed}
              onCheckedChange={(v) => {
                setConfirmed(v === true);
              }}
              className="mt-0.5 cursor-pointer"
            />
            <label htmlFor="meta-confirm-checkbox" className="cursor-pointer select-none">
              <p className="text-stage-text text-sm font-medium">
                I've confirmed the Lily feed is active in Meta Commerce Manager
              </p>
              <p className="text-stage-text text-xs leading-5">
                Lily will validate that enrichments are live within 24 hours.
              </p>
            </label>
          </div>
          <Button
            className={cn(
              'bg-gradient-btn shrink-0 gap-2',
              confirmed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
            )}
            onClick={() => {
              if (!confirmed) return;
              onConfirm();
            }}
            aria-disabled={!confirmed}
            data-testid={`${testId}-confirm-button`}
          >
            Confirm Setup Complete
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StepBadge({ number, testId }: { number: number; testId: string }) {
  return (
    <span
      data-testid={testId}
      className="bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium"
    >
      {number}
    </span>
  );
}

function PathBox({ text, children }: { text?: string; children?: ReactNode }) {
  return (
    <div className="bg-muted/30 rounded-lg border px-4 py-3">
      <p className="text-stage-text text-sm">{children ?? text}</p>
    </div>
  );
}

function InfoSubBox({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-muted/30 space-y-1 rounded-lg border px-4 py-3">
      <p className="text-stage-text text-sm font-semibold">{title}</p>
      <p className="text-stage-text text-sm leading-5">{body}</p>
    </div>
  );
}
